import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaEnvelope, FaLock, FaGoogle, FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa';
import TermsAndConditions from './TermsAndConditions';

export function useHandleSignUp() {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);

  const handleSignUp = async (event) => {
    event.preventDefault();

    const formData = new FormData(event.target);
    const email = formData.get('email') || '';
    const password = formData.get('password') || '';
    const name = formData.get('name') || '';



    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/signup`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'Api-Name': 'auth-api',
        },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (data.message === 'User Created') {
        navigate('/verify', { state: { username: email } });
      } else {
        throw new Error(data.message || 'Signup failed');
      }
    } catch (error) {
      console.error('Error during signup:', error);
    }
  }

  return handleSignUp;
}

export default function SignUp() {
  const handleSignUp = useHandleSignUp();
  const [focusedInput, setFocusedInput] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // New state for password
  const [password, setPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({
    length: true,
    specialChars: true
  });
  const [touchedPassword, setTouchedPassword] = useState(false);
  
  const navigate = useNavigate();

  // Password validation effect
  useEffect(() => {
    if (password) {
      const lengthValid = password.length >= 8;
      
      // Fix the regex to properly count special characters
      const specialChars = password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/g);
      const specialCharsCount = specialChars ? specialChars.join('').length : 0;
      const specialCharsValid = specialCharsCount >= 1;
      
      setPasswordErrors({
        length: !lengthValid,
        specialChars: !specialCharsValid
      });
    }
  }, [password]);

  const openTermsModal = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
  };

  const acceptTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const inputVariants = {
    focused: {
      scale: 1.02,
      boxShadow: "0px 0px 8px rgba(251, 188, 4, 0.3)",
      transition: { type: "spring", stiffness: 300, damping: 15 }
    },
    unfocused: {
      scale: 1,
      boxShadow: "none",
      transition: { type: "spring", stiffness: 300, damping: 15 }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    const formData = new FormData(e.target);
    const email = formData.get('email') || '';
    const formPassword = formData.get('password') || '';
    const name = formData.get('name') || '';

    if (!name || !email || !formPassword) {
      setError('All fields are required');
      return;
    }
    
    // Check password validation before submitting
    if (passwordErrors.length || passwordErrors.specialChars) {
      setError('Please fix password requirements before submitting');
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'auth-api',
        },
        body: JSON.stringify({ name, email, password: formPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Signup failed');
      }

      // Navigate to verify page with email in state
      navigate('/verify', { state: { username: email } });
    } catch (error) {
      console.error('Signup error:', error);
      // Provide more meaningful error message based on the type of error
      if (error.message === 'Failed to fetch') {
        setError('Unable to connect to the server. Try again later.');
      } else if (error.message.includes('NetworkError') || error.message.includes('Network request failed')) {
        setError('Network error occurred. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please try again later.');
      } else {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pri-dark to-sec-dark">
      {/* Animated background elements */}
      <motion.div
        className="absolute top-0 left-0 w-96 h-96 bg-pri-yellow rounded-full mix-blend-multiply filter blur-[128px] opacity-10"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          x: [-50, 0, -50],
          y: [-50, 0, -50],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-96 h-96 bg-pri-green rounded-full mix-blend-multiply filter blur-[128px] opacity-10"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [90, 0, 90],
          x: [50, 0, 50],
          y: [50, 0, 50],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="relative min-h-screen flex items-center justify-center py-2 sm:py-8 px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-md w-full space-y-6 sm:space-y-8 -mt-8 sm:-mt-4"
        >
          {/* Logo and Title Section */}
          <motion.div variants={itemVariants} className="text-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mx-auto w-16 sm:w-20 h-16 sm:h-20 relative cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img
                className="h-16 w-auto mx-auto"
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                alt="Jingleez"
              />
              <motion.div
                className="absolute inset-0 bg-soft-pink rounded-full opacity-20 blur-md"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.3, 0.2],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
            <h2 className="mt-6 text-3xl sm:text-4xl font-extrabold text-soft-pink">
              Create Account
            </h2>
            <p className="mt-2 text-sm text-pri-gray">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-soft-pink hover:text-mint-green transition-colors duration-300">
                Sign In
              </Link>
            </p>
          </motion.div>

          {/* Signup Form Section */}
          <motion.div 
            variants={itemVariants} 
            className="relative overflow-hidden rounded-2xl p-8 
                     bg-gradient-to-br from-soft-pink/10 to-soft-pink/5 
                     backdrop-blur-sm shadow-lg border border-white/10
                     transform-gpu"
            style={{
              transform: 'perspective(1000px) rotateX(2deg)',
            }}
          >
            {/* Glass-like highlight effect at the top */}
            <div className="absolute top-0 left-0 right-0 h-px bg-white/20"></div>
            
            <motion.div variants={itemVariants}>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <motion.div 
                  variants={itemVariants} 
                  className="relative"
                >
                  <motion.div
                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    animate={{ color: focusedInput === 'name' ? "#eec9d9" : "#A0AEC0" }}
                  >
                    <FaUser className="h-5 w-5" />
                  </motion.div>
                  <motion.input
                    variants={inputVariants}
                    animate={focusedInput === 'name' ? 'focused' : 'unfocused'}
                    type="text"
                    name="name"
                    required
                    onFocus={() => setFocusedInput('name')}
                    onBlur={() => setFocusedInput(null)}
                    className="block w-full pl-10 pr-10 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                    placeholder="Full name"
                  />
                </motion.div>

                {/* Email Field */}
                <motion.div 
                  variants={itemVariants} 
                  className="relative"
                >
                  <motion.div
                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    animate={{ color: focusedInput === 'email' ? "#eec9d9" : "#A0AEC0" }}
                  >
                    <FaEnvelope className="h-5 w-5" />
                  </motion.div>
                  <motion.input
                    variants={inputVariants}
                    animate={focusedInput === 'email' ? 'focused' : 'unfocused'}
                    type="email"
                    name="email"
                    required
                    onFocus={() => setFocusedInput('email')}
                    onBlur={() => setFocusedInput(null)}
                    className="block w-full pl-10 pr-10 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                    placeholder="Email address"
                  />
                </motion.div>

                {/* Password Field */}
                <motion.div 
                  variants={itemVariants} 
                  className="relative"
                >
                  <motion.div
                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    animate={{ color: focusedInput === 'password' ? "#eec9d9" : "#A0AEC0" }}
                  >
                    <FaLock className="h-5 w-5" />
                  </motion.div>
                  <motion.input
                    variants={inputVariants}
                    animate={focusedInput === 'password' ? 'focused' : 'unfocused'}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => {
                      setFocusedInput('password');
                      setTouchedPassword(true);
                    }}
                    onBlur={() => setFocusedInput(null)}
                    className="block w-full pl-10 pr-10 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash className="h-5 w-5 text-pri-gray" /> : <FaEye className="h-5 w-5 text-pri-gray" />}
                  </button>
                </motion.div>
                
                {/* Password validation requirements */}
                {touchedPassword && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="text-sm px-3 py-2 bg-pri-dark/30 rounded-lg border border-pri-gray/20"
                  >
                    <p className="text-soft-pink/80 font-medium mb-1">Password requirements:</p>
                    <ul className="space-y-1">
                      <li className="flex items-center">
                        {!passwordErrors.length ? (
                          <FaCheck className="text-mint-green mr-2" />
                        ) : (
                          <FaTimes className="text-red-400 mr-2" />
                        )}
                        <span className={!passwordErrors.length ? "text-mint-green" : "text-pri-gray"}>
                          At least 8 characters
                        </span>
                      </li>
                      <li className="flex items-center">
                        {!passwordErrors.specialChars ? (
                          <FaCheck className="text-mint-green mr-2" />
                        ) : (
                          <FaTimes className="text-red-400 mr-2" />
                        )}
                        <span className={!passwordErrors.specialChars ? "text-mint-green" : "text-pri-gray"}>
                          At least 1 special characters (e.g. @#$%&)
                        </span>
                      </li>
                    </ul>
                  </motion.div>
                )}

                {/* General form error display */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-500/20 text-red-200 px-4 py-2 rounded-lg text-sm"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Terms and Conditions */}
                <motion.div variants={itemVariants} className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      required
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="h-4 w-4 bg-pri-dark border-pri-gray text-pri-dark focus:ring-soft-pink rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="text-pri-gray">
                      I agree to the{' '}
                      <a 
                        href="#" 
                        onClick={openTermsModal}
                        className="text-soft-pink hover:text-mint-green transition-colors"
                      >
                        Terms and Conditions
                      </a>
                    </label>
                  </div>
                </motion.div>

                {/* Submit Button */}
                <motion.button
                  variants={itemVariants}
                  type="submit"
                  disabled={!termsAccepted || isLoading || passwordErrors.length || passwordErrors.specialChars}
                  className={`w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-full shadow-sm text-pri-dark 
                    ${termsAccepted && !isLoading && !passwordErrors.length && !passwordErrors.specialChars
                      ? 'bg-mint-green hover:bg-opacity-90' 
                      : 'bg-mint-green/50 cursor-not-allowed'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-pink transition-colors duration-200 relative overflow-hidden`}
                >
                  {isLoading ? 'Signing Up...' : 'Sign Up'}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditions 
        isOpen={showTermsModal}
        onClose={closeTermsModal}
        onAccept={acceptTerms}
      />
    </div>
  );
}
